import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Info, Users2, ChevronDown, ChevronUp } from "lucide-react";

const AGREE_BG = {
  "Strong agreement": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Moderate agreement": "bg-teal-50 text-teal-700 border-teal-200",
  "Mixed views": "bg-amber-50 text-amber-700 border-amber-200",
  "Active disagreement": "bg-red-50 text-red-700 border-red-200",
};
const EV_BG = {
  "Substantial evidence": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Moderate evidence": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Limited evidence": "bg-amber-50 text-amber-700 border-amber-200",
  "Insufficient evidence": "bg-slate-100 text-slate-500 border-slate-300",
};
const AG_ROWS = [["Strong agreement", "Strong / moderate agreement", (t) => ["Strong agreement", "Moderate agreement"].includes(t.agreement_state)],
  ["Mixed views", "Mixed views", (t) => t.agreement_state === "Mixed views"],
  ["Active disagreement", "Active disagreement / insufficient", (t) => t.agreement_state === "Active disagreement"]];
const EV_COLS = [["Insufficient evidence", "Limited / insufficient evidence", (t) => ["Limited evidence", "Insufficient evidence"].includes(t.evidence_label)],
  ["Moderate evidence", "Moderate evidence", (t) => t.evidence_label === "Moderate evidence"],
  ["Substantial evidence", "Substantial evidence", (t) => t.evidence_label === "Substantial evidence"]];
const QUADRANT = {
  "Strong agreement|Substantial evidence": "Agreement supported by stronger evidence",
  "Strong agreement|Insufficient evidence": "Agreement exceeds available evidence",
  "Active disagreement|Substantial evidence": "Evidence has not translated into agreement",
  "Active disagreement|Insufficient evidence": "Uncertainty in both evidence and HCP position",
};
const TABS = [["overview", "Overview"], ["agreement", "Areas of Agreement"], ["controversies", "Active Controversies"], ["variation", "Variation & Unresolved Questions"]];

function FindingCard({ t, onOpen, full }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm ${full ? "lg:col-span-2" : ""}`} data-testid={`consensus-card-${t.id}`}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        <Badge variant="outline" className={`text-[9px] font-semibold ${AGREE_BG[t.agreement_state]}`}>{t.agreement_state}</Badge>
        <Badge variant="outline" className={`text-[9px] font-semibold ${EV_BG[t.evidence_label]}`}>{t.evidence_label}</Badge>
        <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-500 border-slate-200">{t.variation_type}</Badge>
        {t.disagreement_type && <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">{t.disagreement_type}</Badge>}
        <span className="text-[9px] font-mono uppercase text-amber-600/80 ml-auto">Illustrative demonstration data</span>
      </div>
      <h4 className="text-sm font-bold text-slate-900 leading-snug">{t.topic}</h4>
      <p className="text-xs text-slate-600 leading-relaxed mt-1.5">{t.summary}</p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5 text-[10.5px] text-slate-500">
        <span className="flex items-center gap-1"><Users2 className="w-3 h-3 text-teal-600" /><span className="font-semibold text-slate-700">{t.voice_count} independent HCP voices</span> · {t.source_count} sources (distinct measures)</span>
        <span>Relationship: <span className="font-medium text-slate-700">{t.evidence_relationship}</span></span>
        <span>{t.confidence}</span>
        <button className="text-teal-700 font-semibold hover:underline" onClick={() => onOpen(t)} data-testid={`consensus-open-${t.id}`}>How assessed?</button>
      </div>
      {t.flags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {t.flags.map((f) => <span key={f} className="text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">{f}</span>)}
        </div>
      )}
      {t.controversy && (
        <div className="mt-3 border-t border-slate-100 pt-2.5">
          <button className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 hover:text-slate-800" onClick={() => setOpen(!open)} data-testid={`positions-toggle-${t.id}`}>
            Competing positions · symmetric view {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {open && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid={`positions-${t.id}`}>
              {t.controversy.competing_positions.map((p, i) => (
                <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5">
                  <p className="text-xs text-slate-800 font-medium leading-snug">{p.position}</p>
                  <p className="text-[10px] text-slate-500 mt-1">Held by: {p.holders} · ~{Math.max(3, Math.round(t.voice_count / 2) - i * 2)} voices (illustrative) · Conviction: {i === 0 ? "High" : "Moderate"}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Neither position is presented as correct pending expert review.</p>
                </div>
              ))}
              <div className="sm:col-span-2 grid grid-cols-2 gap-2.5 text-[11px]">
                <div><p className="font-mono text-[9px] uppercase text-emerald-700">Supporting evidence</p><p className="text-slate-600">{t.controversy.supporting_evidence.join(" · ")}</p></div>
                <div><p className="font-mono text-[9px] uppercase text-amber-700">Qualifying / contradictory</p><p className="text-slate-600">{t.controversy.contradicting_evidence?.join(" · ") || "—"}</p></div>
                <div className="col-span-2"><p className="font-mono text-[9px] uppercase text-slate-400">Why it matters</p><p className="text-slate-600">{t.controversy.why_matters}</p></div>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="bg-slate-900 rounded-lg p-3 mt-3" data-testid={`response-${t.id}`}>
        <p className="font-mono text-[9px] uppercase tracking-widest text-teal-400 mb-1">Potential evidence-generation or engagement response
          <span className="ml-2 text-[8.5px] text-violet-300 bg-violet-500/15 border border-violet-500/40 border-dashed rounded-full px-1.5 py-0.5 normal-case">AI-generated draft — expert validation required</span></p>
        <p className="text-[11px] text-slate-300 leading-snug">{t.controversy?.resolution || `Structured evidence review and targeted scientific exchange addressing: ${t.drivers[0]}.`}</p>
        <p className="text-[9.5px] text-slate-500 mt-1">Stakeholder: Medical Affairs / Evidence Generation · Priority: {t.consensus_score < 45 ? "High" : "Medium"} · {t.confidence} · Limitation: based on illustrative demonstration intelligence.</p>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">{t.review_status} · statuses simulate the intended workflow on demonstration content</p>
    </div>
  );
}

export default function Consensus() {
  const [topics, setTopics] = useState([]);
  const [tab, setTab] = useState("overview");
  const [drawer, setDrawer] = useState(null);

  useEffect(() => { api.get("/consensus").then((r) => setTopics(r.data.topics)); }, []);

  const agreement = topics.filter((t) => ["Strong agreement", "Moderate agreement"].includes(t.agreement_state));
  const controversies = topics.filter((t) => t.agreement_state === "Active disagreement" || t.disagreement_type === "Active clinical disagreement" || t.disagreement_type === "Conflicting interpretation");
  const variation = topics.filter((t) => !agreement.includes(t) && !controversies.includes(t));
  const grid2 = (list) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {list.map((t) => <FindingCard key={t.id} t={t} onOpen={setDrawer} full={list.length === 1} />)}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="consensus-view">
      <p className="text-[11px] text-slate-500 bg-white border border-slate-200/80 rounded-xl px-4 py-2.5 flex items-start gap-1.5" data-testid="consensus-methodology">
        <Info className="w-3.5 h-3.5 text-teal-600 shrink-0 mt-0.5" />
        Consensus describes the degree and diversity of agreement among the assessed HCP voices. It does not establish clinical correctness. Evidence strength is assessed separately using verified scientific evidence. All content is illustrative demonstration data; statuses simulate the intended workflow.
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {TABS.map(([id, label]) => (
          <button key={id} data-testid={`consensus-tab-${id}`} onClick={() => setTab(id)}
            className={`text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-700"}`}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid="consensus-quadrant">
            <h3 className="font-heading text-base font-bold text-slate-900 mb-1">HCP agreement vs evidence strength — categorical 3×3 view</h3>
            <p className="text-[10.5px] text-slate-500 mb-3">Inputs are qualitative; a categorical matrix is used instead of numeric placement. Click any finding to see how it was assessed.</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead><tr>
                  <th className="w-40" />
                  {EV_COLS.map(([, label]) => <th key={label} className="text-[9.5px] font-mono uppercase tracking-widest text-slate-400 pb-2 text-left px-2">{label}</th>)}
                </tr></thead>
                <tbody>
                  {AG_ROWS.map(([rowKey, rowLabel, rowFn]) => (
                    <tr key={rowLabel}>
                      <td className="text-[9.5px] font-mono uppercase tracking-widest text-slate-400 pr-2 py-1 align-top w-40">{rowLabel}</td>
                      {EV_COLS.map(([colKey, colLabel, colFn]) => {
                        const cellTopics = topics.filter((t) => rowFn(t) && colFn(t));
                        const q = QUADRANT[`${rowKey}|${colKey}`];
                        return (
                          <td key={colLabel} className="border border-slate-100 bg-slate-50/40 align-top p-2 min-w-[190px]">
                            {q && <p className="text-[8.5px] font-mono uppercase tracking-wide text-slate-400 mb-1">{q}</p>}
                            <div className="flex flex-wrap gap-1">
                              {cellTopics.map((t) => (
                                <button key={t.id} onClick={() => setDrawer(t)} data-testid={`matrix-point-${t.id}`} title={t.topic}
                                  className={`text-[10px] font-medium rounded-full border px-2 py-0.5 hover:ring-1 hover:ring-teal-300 ${AGREE_BG[t.agreement_state]}`}>
                                  {t.id.toUpperCase()} · {t.topic.slice(0, 26)}…
                                </button>
                              ))}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
              {Object.entries(AGREE_BG).map(([l, cls]) => <span key={l} className={`text-[9px] border rounded-full px-2 py-0.5 ${cls}`}>{l}</span>)}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[["Top areas of agreement", agreement.slice(0, 3), "agreement"], ["Most consequential controversies", controversies.slice(0, 3), "controversies"], ["Variation & unresolved questions", variation.slice(0, 3), "variation"]].map(([title, list, target]) => (
              <div key={title} className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">{title}</p>
                {list.map((t) => (
                  <button key={t.id} onClick={() => setTab(target)} className="block text-left text-xs text-slate-700 hover:text-teal-700 mb-1.5" data-testid={`overview-item-${t.id}`}>
                    • {t.topic} <span className="text-[9.5px] text-slate-400">({t.agreement_state} · {t.evidence_label} · {t.voice_count} voices)</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === "agreement" && grid2(agreement)}
      {tab === "controversies" && grid2(controversies)}
      {tab === "variation" && grid2(variation)}

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="consensus-drawer">
          {drawer && (
            <>
              <SheetHeader><SheetTitle className="font-heading text-base text-left">{drawer.topic}</SheetTitle></SheetHeader>
              <div className="space-y-2.5 mt-4">
                {drawer.reproducible_scoring && (
                  <div className="bg-teal-50/60 border border-teal-200 rounded-lg px-3 py-2" data-testid="consensus-reproducible-score">
                    <p className="font-mono text-[8.5px] uppercase tracking-widest text-teal-700">Reproducible calibrated output · {drawer.reproducible_scoring.calculation_version}</p>
                    <p className="text-xs font-bold text-slate-800 mt-0.5">{drawer.reproducible_scoring.label}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{drawer.reproducible_scoring.basis}</p>
                  </div>
                )}
                {[["HCP agreement", `${drawer.agreement_state} (internal working score ${drawer.consensus_score}/100 — superseded by the categorical calibrated output above for reporting)`],
                  ["Evidence strength", `${drawer.evidence_label} (internal score ${drawer.evidence_strength}/100)`],
                  ["Assessment confidence", drawer.confidence],
                  ["Independent HCP voices", `${drawer.voice_count} (deduplicated — repeated statements and multi-site reports of the same session are not counted twice)`],
                  ["Independent sources", drawer.source_count],
                  ["Evidence relationship", drawer.evidence_relationship],
                  ["Variation type", drawer.variation_type],
                  ["Disagreement type", drawer.disagreement_type || "Not applicable"],
                  ["Drivers", drawer.drivers.join(" · ")],
                  ["How assessed", drawer.method_note],
                  ["Flags", drawer.flags.join(" · ")],
                  ["Review status", drawer.review_status]].map(([l, v]) => (
                  <div key={l}><p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">{l}</p><p className="text-[11px] text-slate-700 leading-snug">{v}</p></div>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
